import Service, { type Registry as Services, service } from '@ember/service';
import { task } from 'ember-concurrency';
import type {
  Carate,
  Carecommendation,
  Period,
  PriceAction,
} from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

interface RecommendationsAllRoomsRequestParams {
  from_date_range_end: string;
  from_date_range_start: string;
  hotel_id: string;
  namespace: typeof APIGATEWAY;
  typeKey: string;
}

interface PriceActionsRequestParams {
  from_date_range_end: string;
  from_date_range_start: string;
  namespace: typeof APIGATEWAY;
  subscription_id: string;
}

interface PricingPmsRoomsAllRequestParams {
  from_date_range_end: string;
  from_date_range_start: string;
  hotel_id: string;
  namespace: typeof APIGATEWAY;
  typeKey: string;
}

interface RatesAllChannelsRequestParams {
  compare_extract_date: string;
  from_date_range_end: string;
  from_date_range_start: string;
  hotel_id: string;
  namespace: typeof APIGATEWAY;
  typeKey: string;
}

export default class ChannelPricingDataRequestService extends Service {
  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare store: Services['store'];

  priceActionsDataRequestTask = task(
    { restartable: true },
    async (hotelId: string, rangeStart: string, rangeEnd: string) => {
      const queryParams: PriceActionsRequestParams = {
        namespace: APIGATEWAY,
        from_date_range_start: rangeStart,
        from_date_range_end: rangeEnd,
        subscription_id: hotelId,
      };

      try {
        const priceActions = await this.store.query<PriceAction>(
          'price-action',
          {
            ...queryParams,
            typeKey: 'pricing-assistant/recommendations/action/status/latest',
          },
        );

        return [...priceActions];
      } catch (error) {
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );
  pricingPmsRoomsAllRequestTask = task(
    {},
    async (hotelId: string, rangeStart: string, rangeEnd: string) => {
      try {
        const requestParams: PricingPmsRoomsAllRequestParams = {
          namespace: APIGATEWAY,
          typeKey: 'pricing-assistant/rates/pms/rooms/all',
          from_date_range_start: rangeStart,
          from_date_range_end: rangeEnd,
          hotel_id: hotelId,
        };

        const carates = await this.store.query<Carate>('carate', {
          ...requestParams,
        });

        return [...carates];
      } catch (error) {
        void this.alerting.showErrorAlert();
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  ratesAllChannelsRequestTask = task(
    {},
    async (hotelId: string, rangeStart: string, rangeEnd: string) => {
      const compareDate = this.hotelAndMarketService
        .getLocalTime()
        .subtract(1, 'day')
        .format('YYYY-MM-DD');

      try {
        const requestParams: RatesAllChannelsRequestParams = {
          namespace: APIGATEWAY,
          typeKey: 'pricing-assistant/rates/otas/all',
          from_date_range_start: rangeStart,
          from_date_range_end: rangeEnd,
          compare_extract_date: compareDate,
          hotel_id: hotelId,
        };

        const periods = await this.store.query<Period>('period', {
          ...requestParams,
        });
        return [...periods];
      } catch (error) {
        void this.alerting.showErrorAlert();
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  recommendationsForAllRoomsRequestTask = task(
    {},
    async (hotelId: string, rangeStart: string, rangeEnd: string) => {
      try {
        const requestParams: RecommendationsAllRoomsRequestParams = {
          namespace: APIGATEWAY,
          typeKey: 'pricing-assistant/recommendations/rooms/all',
          from_date_range_start: rangeStart,
          from_date_range_end: rangeEnd,
          hotel_id: hotelId,
        };

        const response = await this.store.query<Carecommendation>(
          'carecommendation',
          { ...requestParams },
        );

        return [...response];
      } catch (error) {
        void this.alerting.showErrorAlert();
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  clearCarecommendationsForCertainDay(day: string): void {
    const carecommendations =
      this.store.peekAll<Carecommendation>('carecommendation');

    carecommendations.forEach((carecommendation) => {
      if (carecommendation.fromDate.isSame(day, 'day')) {
        this.store.unloadRecord(carecommendation);
      }
    });
  }

  async onChannelPricingPollingFinished(
    hotelId: string,
    date: string,
  ): Promise<{ carecommendation: Carecommendation[] }> {
    // Unload the recommendations of the day being pushed before re-launching the recommendationsForAllRoomsRequestTask
    // It's possible that no recommendation is returned after actioning one, and in that case we need to unload our local one
    // We used to have a rollbackAttributes here, as our current local carecommendation normally is marked as dirty
    // The unload is a bit more aggressive, but it ensures we always end up in the correct state after the reload

    this.clearCarecommendationsForCertainDay(date);

    // CLOSING THE LOOP TODO: Refresh price actions manually for now
    // Technically we don't need to wait for the push to finish
    // Potential future improvement: would be nice to immediately get it in the store when we create the price action before pushing a price
    // Adding this here now for initial quickness & least impact
    if (this.access.hasSmartDistributionClosingTheLoopFeature) {
      void this.priceActionsDataRequestTask.perform(hotelId, date, date);
    }

    // launch 3 requests:
    // - to re-request the recommendations for all own hotel rooms for the impacted day
    // - to refresh all (PMS) rates of the own hotel in the PMS table for the impacted day
    // - to refresh the (channel) rates shown in the channels table for the impacted day
    const result = await Promise.all([
      this.recommendationsForAllRoomsRequestTask.perform(hotelId, date, date),
      this.pricingPmsRoomsAllRequestTask.perform(hotelId, date, date),
      this.ratesAllChannelsRequestTask.perform(hotelId, date, date),
    ]);

    // return value isn't really used atm. Can be evaluated later
    return { carecommendation: result[0] };
  }
}

declare module '@ember/service' {
  interface Registry {
    'channel-pricing/data-request': ChannelPricingDataRequestService;
  }
}
